import { type Position, Terrain, Animal, EdgeType, Direction } from "./types"
import { Grid } from "./grid"
import { Tile } from "./tile"

// ── Colors ──

const TERRAIN_COLORS: Record<Terrain, string> = {
  [Terrain.Prairie]: "#a8d5a2",
  [Terrain.Forest]: "#4a8c3f",
  [Terrain.Mountain]: "#9e9e9e",
  [Terrain.Lake]: "#5b9bd5",
}

const TERRAIN_COLORS_HOVER: Record<Terrain, string> = {
  [Terrain.Prairie]: "#c2e8bc",
  [Terrain.Forest]: "#5ea650",
  [Terrain.Mountain]: "#b3b3b3",
  [Terrain.Lake]: "#7bb3e8",
}

const ANIMAL_EMOJI: Record<Animal, string> = {
  [Animal.Sheep]: "\u{1F411}",
  [Animal.Deer]: "\u{1F98C}",
  [Animal.Wolf]: "\u{1F43A}",
  [Animal.Bear]: "\u{1F43B}",
}

const EDGE_INDICATOR_COLORS: Record<EdgeType, string | null> = {
  [EdgeType.Land]: null,
  [EdgeType.Water]: "#3b82f6",
  [EdgeType.Cliff]: "#78350f",
}

const VALID_PLACEMENT_COLOR = "rgba(255, 255, 255, 0.3)"
const VALID_PLACEMENT_BORDER = "rgba(255, 255, 255, 0.6)"
const GRID_LINE_COLOR = "rgba(0, 0, 0, 0.1)"
const GRID_BG_COLOR = "#e8e0d4"

export interface RendererConfig {
  cellSize: number
  padding: number
}

export class Renderer {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private cellSize: number
  private padding: number

  constructor(canvas: HTMLCanvasElement, config?: Partial<RendererConfig>) {
    this.canvas = canvas
    this.ctx = canvas.getContext("2d")!
    this.cellSize = config?.cellSize ?? 56
    this.padding = config?.padding ?? 16
  }

  /** Resize canvas to fit grid */
  resize(gridSize: number): void {
    const totalSize = gridSize * this.cellSize + this.padding * 2
    const dpr = window.devicePixelRatio || 1
    this.canvas.width = totalSize * dpr
    this.canvas.height = totalSize * dpr
    this.canvas.style.width = `${totalSize}px`
    this.canvas.style.height = `${totalSize}px`
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  /** Convert pixel coordinates to grid position */
  pixelToGrid(px: number, py: number): Position | null {
    const col = Math.floor((px - this.padding) / this.cellSize)
    const row = Math.floor((py - this.padding) / this.cellSize)
    if (row < 0 || col < 0) return null
    return { row, col }
  }

  /** Get pixel coordinates for a grid position (top-left corner of cell) */
  gridToPixel(pos: Position): { x: number; y: number } {
    return {
      x: this.padding + pos.col * this.cellSize,
      y: this.padding + pos.row * this.cellSize,
    }
  }

  /** Full render pass */
  render(
    grid: Grid,
    validPlacements: Position[],
    hoveredCell: Position | null,
    selectedTile: Tile | null,
  ): void {
    const ctx = this.ctx
    const size = grid.size

    // Clear
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    // Background
    ctx.fillStyle = GRID_BG_COLOR
    ctx.fillRect(
      this.padding, this.padding,
      size * this.cellSize, size * this.cellSize,
    )

    // Grid lines
    ctx.strokeStyle = GRID_LINE_COLOR
    ctx.lineWidth = 1
    for (let i = 0; i <= size; i++) {
      const x = this.padding + i * this.cellSize
      const y = this.padding + i * this.cellSize
      ctx.beginPath()
      ctx.moveTo(x, this.padding)
      ctx.lineTo(x, this.padding + size * this.cellSize)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(this.padding, y)
      ctx.lineTo(this.padding + size * this.cellSize, y)
      ctx.stroke()
    }

    // Placed tiles
    grid.forEachTile((pos, tile) => {
      this.drawTile(pos, tile)
    })

    // Valid placement highlights
    const validSet = new Set(validPlacements.map(p => `${p.row},${p.col}`))
    for (const pos of validPlacements) {
      this.drawValidPlacement(pos)
    }

    // Hover preview
    if (hoveredCell && selectedTile && validSet.has(`${hoveredCell.row},${hoveredCell.col}`)) {
      this.drawTilePreview(hoveredCell, selectedTile)
    }
  }

  private drawTile(pos: Position, tile: Tile): void {
    const ctx = this.ctx
    const { x, y } = this.gridToPixel(pos)
    const cs = this.cellSize

    // Fill terrain color
    ctx.fillStyle = TERRAIN_COLORS[tile.terrain]
    ctx.fillRect(x + 1, y + 1, cs - 2, cs - 2)

    // Edge indicators
    this.drawEdgeIndicators(x, y, cs, tile)

    // Animal emoji
    if (tile.animal) {
      ctx.font = `${cs * 0.45}px serif`
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText(ANIMAL_EMOJI[tile.animal], x + cs / 2, y + cs / 2 + 2)
    }
  }

  private drawEdgeIndicators(x: number, y: number, cs: number, tile: Tile): void {
    const ctx = this.ctx
    const thickness = 3

    const edges: [Direction, number, number, number, number][] = [
      [Direction.N, x + 1, y + 1, cs - 2, thickness],
      [Direction.S, x + 1, y + cs - thickness - 1, cs - 2, thickness],
      [Direction.W, x + 1, y + 1, thickness, cs - 2],
      [Direction.E, x + cs - thickness - 1, y + 1, thickness, cs - 2],
    ]

    for (const [dir, ex, ey, ew, eh] of edges) {
      const edgeType = tile.getEdge(dir)
      const color = EDGE_INDICATOR_COLORS[edgeType]
      if (color) {
        ctx.fillStyle = color
        ctx.fillRect(ex, ey, ew, eh)
      }
    }
  }

  private drawValidPlacement(pos: Position): void {
    const ctx = this.ctx
    const { x, y } = this.gridToPixel(pos)
    const cs = this.cellSize

    ctx.fillStyle = VALID_PLACEMENT_COLOR
    ctx.fillRect(x + 1, y + 1, cs - 2, cs - 2)

    ctx.strokeStyle = VALID_PLACEMENT_BORDER
    ctx.lineWidth = 2
    ctx.setLineDash([4, 4])
    ctx.strokeRect(x + 2, y + 2, cs - 4, cs - 4)
    ctx.setLineDash([])
  }

  private drawTilePreview(pos: Position, tile: Tile): void {
    const ctx = this.ctx
    const { x, y } = this.gridToPixel(pos)
    const cs = this.cellSize

    ctx.globalAlpha = 0.6
    ctx.fillStyle = TERRAIN_COLORS_HOVER[tile.terrain]
    ctx.fillRect(x + 1, y + 1, cs - 2, cs - 2)
    this.drawEdgeIndicators(x, y, cs, tile)

    if (tile.animal) {
      ctx.font = `${cs * 0.45}px serif`
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText(ANIMAL_EMOJI[tile.animal], x + cs / 2, y + cs / 2 + 2)
    }

    ctx.globalAlpha = 1.0
  }

  /** Draw a single tile in a given pixel area (for market card preview) */
  drawTileCard(
    targetCanvas: HTMLCanvasElement,
    tile: Tile,
    size: number,
  ): void {
    const ctx = targetCanvas.getContext("2d")!
    const dpr = window.devicePixelRatio || 1
    targetCanvas.width = size * dpr
    targetCanvas.height = size * dpr
    targetCanvas.style.width = `${size}px`
    targetCanvas.style.height = `${size}px`
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    ctx.fillStyle = TERRAIN_COLORS[tile.terrain]
    ctx.fillRect(1, 1, size - 2, size - 2)

    // Edge indicators
    const thickness = 3
    const edges: [Direction, number, number, number, number][] = [
      [Direction.N, 1, 1, size - 2, thickness],
      [Direction.S, 1, size - thickness - 1, size - 2, thickness],
      [Direction.W, 1, 1, thickness, size - 2],
      [Direction.E, size - thickness - 1, 1, thickness, size - 2],
    ]

    for (const [dir, ex, ey, ew, eh] of edges) {
      const edgeType = tile.getEdge(dir)
      const color = EDGE_INDICATOR_COLORS[edgeType]
      if (color) {
        ctx.fillStyle = color
        ctx.fillRect(ex, ey, ew, eh)
      }
    }

    if (tile.animal) {
      ctx.font = `${size * 0.45}px serif`
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText(ANIMAL_EMOJI[tile.animal], size / 2, size / 2 + 2)
    }
  }
}
